import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "./firebase";
import { ref, get, set, onValue } from "firebase/database";
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";

const inputStyle = { padding: 8, border: "1px solid #ccc", borderRadius: 8, fontSize: 14, fontFamily: "'Roboto', Roboto" };
const btnStyle = { padding: "8px 14px", background: "#007bff", color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", marginRight: 10, fontFamily: "'Roboto', Roboto" };
const imageStyle = { width: 300, height: 300, borderRadius: 8, objectFit: "cover", marginBottom: 10 };
const containerStyle = { padding: 12, maxWidth: 2000, margin: "0 auto", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "20vh", fontFamily: "'Roboto', Roboto" };

export default function EditProductPage({ uid }) {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [preview, setPreview] = useState("");
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);

  // Load brands
  useEffect(() => {
    const brandsRef = ref(db, `distributors/${uid}/brands`);
    const unsub = onValue(brandsRef, snap => {
      const val = snap.val();
      setBrands(val ? Object.keys(val).map(id => ({ id, ...val[id] })) : []);
    });
    return () => unsub();
  }, [uid]);

  // Load categories
  useEffect(() => {
    const categoriesRef = ref(db, `distributors/${uid}/categories`);
    const unsub = onValue(categoriesRef, snap => {
      const val = snap.val();
      setCategories(val ? Object.keys(val).map(id => ({ id, ...val[id] })) : []);
    });
    return () => unsub();
  }, [uid]);

  // Load product
  useEffect(() => {
    const productRef = ref(db, `distributors/${uid}/products/${productId}`);
    get(productRef).then(snap => {
      if (snap.exists()) {
        const data = snap.val();
        setProduct({
          ...data,
          saleType: data.saleType ?? 0,
          minQuantity: data.minQuantity ?? 1,
          minBoxQuantity: data.minBoxQuantity ?? 1,
          wholesalePrices: data.wholesalePrices || {
            demiGros: { price: 0, saleType: 0, minQtyUnit: 1, minQtyBox: 1 },
            gros: { price: 0, saleType: 0, minQtyUnit: 1, minQtyBox: 1 },
            superGros: { price: 0, saleType: 0, minQtyUnit: 1, minQtyBox: 1 },
          },
        });
        setPreview(data.imageUrl || "");
      }
    });
  }, [uid, productId]);

  if (!product) return <div>Loading...</div>;

  // Handle input changes for basic fields
  const handleBasicChange = (field, value) => {
    setProduct({ ...product, [field]: ["price","purchasePrice","quantity","minQuantity","minBoxQuantity"].includes(field) ? Number(value) : value });
  };

  // Handle wholesale changes
  const handleWholesaleChange = (key, field, value) => {
    const item = product.wholesalePrices[key];
    const updatedItem = { ...item, [field]: ["price","saleType","minQtyUnit","minQtyBox"].includes(field) ? Number(value) : value };
    setProduct({
      ...product,
      wholesalePrices: {
        ...product.wholesalePrices,
        [key]: updatedItem
      }
    });
  };

  // Submit form
  const handleSubmit = async e => {
    e.preventDefault();
    let imageUrl = product.imageUrl;

    if (product.imageFile) {
  const storage = getStorage();

  // حذف الصورة القديمة
  if (product.imageUrl) {
    try {
      const oldImageRef = storageRef(storage, product.imageUrl);
      await deleteObject(oldImageRef);
    } catch (error) {
      console.log("Old image not found or already deleted");
    }
  }

  // رفع الصورة الجديدة
  const imgRef = storageRef(storage, `products/${uid}/${Date.now()}_${product.imageFile.name}`);
  await uploadBytes(imgRef, product.imageFile);
  imageUrl = await getDownloadURL(imgRef);
}

    const productData = {
      id: productId,
      name: product.name || "",
      brandId: product.brandId || "",
      brandName: product.brandName || "",
      categoryId: product.categoryId || "",
      categoryName: product.categoryName || "",
      packaging: product.packaging || "",
      imageUrl: imageUrl || "",
      price: Number(product.price),
      purchasePrice: Number(product.purchasePrice),
      quantity: Number(product.quantity),
      saleType: Number(product.saleType),
      minQuantity: Number(product.saleType === 0 ? product.minQuantity : 0),
      minBoxQuantity: Number(product.saleType === 1 ? product.minBoxQuantity : 0),
      wholesalePrices: {
        demiGros: {
          price: Number(product.wholesalePrices.demiGros.price),
          saleType: Number(product.wholesalePrices.demiGros.saleType),
          minQtyUnit: Number(product.wholesalePrices.demiGros.minQtyUnit),
          minQtyBox: Number(product.wholesalePrices.demiGros.minQtyBox),
        },
        gros: {
          price: Number(product.wholesalePrices.gros.price),
          saleType: Number(product.wholesalePrices.gros.saleType),
          minQtyUnit: Number(product.wholesalePrices.gros.minQtyUnit),
          minQtyBox: Number(product.wholesalePrices.gros.minQtyBox),
        },
        superGros: {
          price: Number(product.wholesalePrices.superGros.price),
          saleType: Number(product.wholesalePrices.superGros.saleType),
          minQtyUnit: Number(product.wholesalePrices.superGros.minQtyUnit),
          minQtyBox: Number(product.wholesalePrices.superGros.minQtyBox),
        },
      },
      createdAt: product.createdAt || Date.now(),
    };

    await set(ref(db, `distributors/${uid}/products/${productId}`), productData);
    alert("✔ Produit modifié avec succès");
    navigate("/products");
  };

  return (
    <div style={containerStyle}>
      <h2>Modifier le produit</h2>
      {preview && <img src={preview} alt="Preview" style={imageStyle} />}
      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "row", gap: 12, flexWrap: "wrap", alignItems: "flex-start", justifyContent: "flex-start", width: "100%" }}>

        {/* Basic Fields */}
        {[
          { label: "Nom de produit", value: "name" },
          { label: "Prix d'achat", value: "purchasePrice", type: "number" },
          { label: "Prix de vente", value: "price", type: "number" },
          { label: "Quantité", value: "quantity", type: "number" },
          { label: "Emballage", value: "packaging" },
        ].map(field => (
          <div key={field.value} style={{ display: "flex", flexDirection: "column", width: "calc(25% - 12px)" }}>
            <label>{field.label}</label>
            <input type={field.type || "text"} value={product[field.value]} onChange={e => handleBasicChange(field.value, e.target.value)} style={inputStyle} />
          </div>
        ))}

        {/* Brand */}
        <div style={{ display: "flex", flexDirection: "column", width: "calc(25% - 12px)" }}>
          <label>Marque</label>
          <select value={product.brandId} onChange={e => {
            const selected = brands.find(b => b.id === e.target.value);
            setProduct({ ...product, brandId: selected.id, brandName: selected.name });
          }} style={inputStyle}>
            <option value="">Sélectionner une marque</option>
            {brands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>

        {/* Category */}
        <div style={{ display: "flex", flexDirection: "column", width: "calc(25% - 12px)" }}>
          <label>Catégorie</label>
          <select value={product.categoryId} onChange={e => {
            const selected = categories.find(c => c.id === e.target.value);
            setProduct({ ...product, categoryId: selected.id, categoryName: selected.name });
          }} style={inputStyle}>
            <option value="">Sélectionner une catégorie</option>
            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>

        {/* Sale Type */}
        <div style={{ display: "flex", flexDirection: "column", width: "calc(25% - 12px)" }}>
          <label>Type de vente</label>
          <select value={product.saleType} onChange={e => {
            const type = Number(e.target.value);
            setProduct({
              ...product,
              saleType: type,
              minQuantity: type === 0 ? Number(product.minQuantity || 1) : 0,
              minBoxQuantity: type === 1 ? Number(product.minBoxQuantity || 1) : 0
            });
          }} style={inputStyle}>
            <option value={0}>Unité</option>
            <option value={1}>Carton</option>
          </select>
        </div>

        {/* Min Quantity / Box */}
        {product.saleType === 0 && (
          <div style={{ display: "flex", flexDirection: "column", width: "calc(25% - 12px)" }}>
            <label>Min unité</label>
            <input type="number" value={product.minQuantity} onChange={e => handleBasicChange("minQuantity", e.target.value)} style={inputStyle} />
          </div>
        )}
        {product.saleType === 1 && (
          <div style={{ display: "flex", flexDirection: "column", width: "calc(25% - 12px)" }}>
            <label>Min carton</label>
            <input type="number" value={product.minBoxQuantity} onChange={e => handleBasicChange("minBoxQuantity", e.target.value)} style={inputStyle} />
          </div>
        )}

       {/* Wholesale Prices */}
{['demiGros', 'gros', 'superGros'].map(key => {
  const labelMap = { demiGros: "Demi Gros", gros: "Gros", superGros: "Super Gros" };
  const item = product.wholesalePrices[key];

  return (
    <div
      key={key}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 6,
        width: "calc(25% - 12px)"
      }}
    >
      <label>{labelMap[key]} Prix</label>

      <input
        type="number"
        value={item.price}
        onChange={e => handleWholesaleChange(key, "price", e.target.value)}
        style={inputStyle}
      />

      <label>Type</label>

      <select
        value={item.saleType}
        onChange={e => {
          const newType = Number(e.target.value);

          setProduct(prev => ({
            ...prev,
            wholesalePrices: {
              ...prev.wholesalePrices,
              [key]: {
                ...prev.wholesalePrices[key],
                saleType: newType,
                minQtyUnit:
                  newType === 0
                    ? prev.wholesalePrices[key].minQtyUnit || 1
                    : 0,
                minQtyBox:
                  newType === 1
                    ? prev.wholesalePrices[key].minQtyBox || 1
                    : 0
              }
            }
          }));
        }}
        style={inputStyle}
      >
        <option value={0}>Unité</option>
        <option value={1}>Carton</option>
      </select>

      {item.saleType === 0 && (
        <input
          type="number"
          value={item.minQtyUnit}
          onChange={e =>
            handleWholesaleChange(key, "minQtyUnit", e.target.value)
          }
          placeholder="Min unité"
          style={inputStyle}
        />
      )}

      {item.saleType === 1 && (
        <input
          type="number"
          value={item.minQtyBox}
          onChange={e =>
            handleWholesaleChange(key, "minQtyBox", e.target.value)
          }
          placeholder="Min carton"
          style={inputStyle}
        />
      )}
    </div>
  );
})}

        {/* Image */}
        <div style={{ display: "flex", flexDirection: "column", width: "calc(100% - 12px)" }}>
          <label>Image du produit</label>
          <input type="file" accept="image/*" onChange={e => {
            const file = e.target.files[0];
            setProduct({ ...product, imageFile: file });
            setPreview(URL.createObjectURL(file));
          }} style={inputStyle} />
        </div>

        {/* Buttons */}
        <div style={{ display: "flex", gap: 12 }}>
          <button type="submit" style={btnStyle}>Enregistrer</button>
          <button type="button" style={{ ...btnStyle, background: "#6c757d" }} onClick={() => navigate("/products")}>Annuler</button>
        </div>
      </form>
    </div>
  );
}